import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { PRODUCT_CATEGORIES } from '../data/mock';
import { MapPin, Loader2, ArrowRight, ArrowLeft, Zap, Sun, Clock, DollarSign, CheckCircle2, Battery, Car, Thermometer, Wind } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { SolarResults } from './SolarResults';
import { BatteryResults } from './BatteryResults';
import { LeadCaptureFlow } from './LeadCaptureFlow';
import { AddressAutocomplete } from './AddressAutocomplete';
import { ExpertBadge } from './ExpertBadge';
import { trackProductSelected, trackAddressEntered, trackLead, trackViewContent } from '../utils/fbPixel';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Product tiles with icons - now includes Heating & Cooling
const PRODUCT_TILES = [
  { 
    id: 'solar', 
    name: 'Solar Systems', 
    icon: Sun, 
    description: 'Reduce your bills',
    color: 'amber',
    hasFullFlow: true
  },
  { 
    id: 'battery', 
    name: 'Battery Storage', 
    icon: Battery, 
    description: 'Store your energy',
    color: 'blue',
    hasFullFlow: true
  },
  { 
    id: 'ev-charger', 
    name: 'EV Chargers', 
    icon: Car, 
    description: 'Charge at home',
    color: 'green',
    hasFullFlow: false
  },
  { 
    id: 'heat-pump', 
    name: 'Heat Pumps', 
    icon: Thermometer, 
    description: 'Hot water systems',
    color: 'red',
    hasFullFlow: false
  },
  { 
    id: 'hvac', 
    name: 'Heating & Cooling', 
    icon: Wind, 
    description: 'Climate control',
    color: 'cyan',
    hasFullFlow: false
  },
];

// Solar system size options
const SOLAR_SYSTEM_SIZES = [
  { value: 'none', label: "I don't have solar yet" },
  { value: '3kw', label: '3kW (Small)' },
  { value: '5kw', label: '5kW (Medium)' },
  { value: '6.6kw', label: '6.6kW (Popular)' },
  { value: '10kw', label: '10kW (Large)' },
  { value: '10kw+', label: '10kW+ (Very Large)' },
];

// Years options
const YEARS_OPTIONS = [
  { value: '0-1', label: 'Less than 1 year' },
  { value: '1-3', label: '1-3 years' },
  { value: '3-5', label: '3-5 years' },
  { value: '5-10', label: '5-10 years' },
  { value: '10+', label: '10+ years' },
];

export const ComparisonModule = ({ onShowOptions }) => {
  // Ref for scrolling
  const sectionRef = useRef(null);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  
  // Step 1 data
  const [selectedProduct, setSelectedProduct] = useState('');
  const [postcode, setPostcode] = useState('');
  const [address, setAddress] = useState('');
  
  // Step 2 data - Common
  const [monthlyBill, setMonthlyBill] = useState(250);
  
  // Step 2 data - Battery specific
  const [solarSystemSize, setSolarSystemSize] = useState('');
  const [solarYears, setSolarYears] = useState('');
  
  // UI state
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Results data
  const [submittedData, setSubmittedData] = useState(null);

  // Optional deep-link: /compare?product=battery jumps straight to the address step
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get('product');
    if (p && PRODUCT_TILES.some((t) => t.id === p)) {
      setSelectedProduct(p);
      setCurrentStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll section into view when step changes
  const scrollToSection = () => {
    if (sectionRef.current) {
      // Get the element's position
      const element = sectionRef.current;
      const headerOffset = 70; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Scroll when step changes to step 2 or when showing results/lead capture
  useEffect(() => {
    if (currentStep === 2 || showResults || showLeadCapture) {
      // Small delay to ensure DOM has updated
      setTimeout(scrollToSection, 100);
    }
  }, [currentStep, showResults, showLeadCapture]);

  // Check if product has full comparison flow
  const productHasFullFlow = (productId) => {
    return productId === 'solar' || productId === 'battery';
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    setError('');

    if (!selectedProduct) {
      setError('Please select a product to compare');
      return;
    }

    // All products go to step 2 for postcode
    setCurrentStep(2);
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    setError('');

    // Validate address is selected
    if (!address || !postcode) {
      setError('Please select your address from the suggestions');
      return;
    }

    // Validation for battery-specific fields
    if (selectedProduct === 'battery') {
      if (!solarSystemSize) {
        setError('Please select your current solar system size');
        return;
      }
      // Only require "how long" if user has solar (not "none")
      if (solarSystemSize !== 'none' && !solarYears) {
        setError('Please select how long you\'ve had your solar system');
        return;
      }
    }

    handleFinalSubmit();
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);

    try {
      const payload = {
        product_type: selectedProduct,
        postcode: postcode,
        address: address || null,
        monthly_bill: monthlyBill,
        ...(selectedProduct === 'battery' && {
          solar_system_size: solarSystemSize,
          solar_years: solarYears,
        }),
      };

      const response = await axios.post(`${BACKEND_URL}/api/enquiries`, payload);
      const data = response.data;
      
      // Track Lead event in Facebook Pixel
      trackLead(selectedProduct, postcode);
      
      // Store submitted data for results
      setSubmittedData({
        ...payload,
        enquiryId: data.id
      });

      // For Solar and Battery, show results page and track journey
      if (selectedProduct === 'solar') {
        // Track that user is viewing results
        axios.patch(`${BACKEND_URL}/api/enquiries/${data.id}/journey`, { step: 'results_viewed' }).catch(() => {});
        trackViewContent('Solar Options', 'Results');
        setShowResults(true);
        toast.success('Options Found!', {
          description: 'Here are your tailored solar system recommendations.',
          duration: 3000,
        });
      } else if (selectedProduct === 'battery') {
        // Track that user is viewing results
        axios.patch(`${BACKEND_URL}/api/enquiries/${data.id}/journey`, { step: 'results_viewed' }).catch(() => {});
        trackViewContent('Battery Options', 'Results');
        setShowResults(true);
        toast.success('Options Found!', {
          description: 'Here are your tailored battery storage recommendations.',
          duration: 3000,
        });
      } else {
        // For EV Chargers, Heat Pumps, and Heating & Cooling - show lead capture
        trackViewContent(selectedProduct, 'Lead Capture');
        setShowLeadCapture(true);
        toast.info('Almost there!', {
          description: 'Enter your details and we\'ll connect you with a specialist.',
          duration: 3000,
        });
      }

    } catch (err) {
      console.error('Enquiry submission error:', err);
      const errorMessage = err.response?.data?.detail || 'Something went wrong. Please try again.';
      
      if (Array.isArray(errorMessage)) {
        setError(errorMessage[0]?.msg || 'Validation error');
      } else {
        setError(errorMessage);
      }
      
      toast.error('Submission Failed', {
        description: 'Please check your inputs and try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setShowResults(false);
    setShowLeadCapture(false);
    setSelectedProduct('');
    setPostcode('');
    setAddress('');
    setMonthlyBill(250);
    setSolarSystemSize('');
    setSolarYears('');
    setSubmittedData(null);
  };

  const goBack = () => {
    if (showResults || showLeadCapture) {
      setShowResults(false);
      setShowLeadCapture(false);
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
    setError('');
  };

  const handleGetQuotes = (option) => {
    toast.success(`Great choice!`, {
      description: `We'll connect you with installers for ${option.name} (${option.systemSizeMin}-${option.systemSizeMax}kW systems).`,
      duration: 5000,
    });
    
    if (onShowOptions) {
      onShowOptions({
        ...submittedData,
        selectedOption: option
      });
    }
  };

  // Get product name for display
  const getProductName = () => {
    const product = PRODUCT_TILES.find(p => p.id === selectedProduct);
    return product?.name || selectedProduct;
  };

  // If showing lead capture flow for non-solar/battery products
  if (showLeadCapture) {
    return (
      <section ref={sectionRef} id="compare" className="relative z-20 px-4 sm:px-6 lg:px-8 py-2">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <LeadCaptureFlow
                productType={selectedProduct}
                productName={getProductName()}
                postcode={postcode}
                address={address}
                enquiryId={submittedData?.enquiryId}
                onBack={goBack}
                onComplete={resetForm}
              />
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // If showing results for Solar
  if (showResults && selectedProduct === 'solar') {
    return (
      <section ref={sectionRef} id="compare" className="relative z-20 px-4 sm:px-6 lg:px-8 py-2">
        <div className="max-w-5xl mx-auto">
          <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <SolarResults 
                monthlyBill={monthlyBill}
                postcode={postcode}
                enquiryId={submittedData?.enquiryId}
                onGetQuotes={handleGetQuotes}
                onBack={resetForm}
              />
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // If showing results for Battery
  if (showResults && selectedProduct === 'battery') {
    return (
      <section ref={sectionRef} id="compare" className="relative z-20 px-4 sm:px-6 lg:px-8 py-2">
        <div className="max-w-5xl mx-auto">
          <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <BatteryResults 
                monthlyBill={monthlyBill}
                postcode={postcode}
                solarSystemSize={solarSystemSize}
                solarYears={solarYears}
                enquiryId={submittedData?.enquiryId}
                onGetQuotes={handleGetQuotes}
                onBack={resetForm}
              />
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} id="compare" className="relative z-20 px-4 sm:px-6 lg:px-8 py-2">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-4 md:p-5">
            {/* Progress indicator - only for solar/battery */}
            {productHasFullFlow(selectedProduct) && currentStep > 1 && (
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-cyo-orange' : 'text-gray-300'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${currentStep >= 1 ? 'bg-cyo-orange text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {currentStep > 1 ? <CheckCircle2 className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="text-xs font-medium hidden sm:inline">Product</span>
                </div>
                <div className={`w-8 h-0.5 ${currentStep >= 2 ? 'bg-cyo-orange' : 'bg-gray-200'}`} />
                <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-cyo-orange' : 'text-gray-300'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${currentStep >= 2 ? 'bg-cyo-orange text-white' : 'bg-gray-200 text-gray-500'}`}>
                    2
                  </div>
                  <span className="text-xs font-medium hidden sm:inline">Details</span>
                </div>
              </div>
            )}

            {/* Step 1: Product Selection Only */}
            {currentStep === 1 && (
              <>
                <div className="text-center mb-5">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
                    What do you want to compare?
                  </h2>
                  <p className="text-gray-500 text-sm">
                    Choose a product to get started
                  </p>
                </div>

                {/* Product Tiles - 5 products in responsive grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {PRODUCT_TILES.map((product, index) => {
                    const Icon = product.icon;
                    const isSelected = selectedProduct === product.id;
                    const flashClass = `tile-flash-${index + 1}`;
                    return (
                      <button
                        key={product.id}
                        type="button"
                        data-testid={`product-tile-${product.id}`}
                        onClick={() => {
                          setSelectedProduct(product.id);
                          setCurrentStep(2);
                          // Track product selection in Facebook Pixel
                          trackProductSelected(product.id);
                        }}
                        className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-center group ${
                          isSelected
                            ? 'border-cyo-orange bg-orange-50 shadow-lg scale-[1.02]'
                            : `border-gray-200 bg-white hover:border-cyo-orange/50 hover:shadow-md hover:scale-[1.01] ${flashClass}`
                        }`}
                      >
                        {/* Large Centered Icon */}
                        <div className={`icon-container w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2 transition-all duration-300 ${
                          isSelected 
                            ? 'bg-cyo-orange/20' 
                            : 'bg-gray-100 group-hover:bg-orange-100'
                        }`}>
                          <Icon className={`icon-element w-6 h-6 transition-colors duration-300 ${
                            isSelected ? 'text-cyo-orange' : 'text-gray-400 group-hover:text-cyo-orange'
                          }`} strokeWidth={1.5} />
                        </div>
                        
                        {/* Text - Centered */}
                        <h3 className={`tile-title font-semibold text-sm mb-0.5 transition-colors duration-300 ${
                          isSelected ? 'text-cyo-orange' : 'text-gray-900'
                        }`}>
                          {product.name}
                        </h3>
                        <p className="tile-desc text-xs text-gray-500">
                          {product.description}
                        </p>

                        {/* "Coming Soon" badge for non-full-flow products */}
                        {!product.hasFullFlow && (
                          <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                            NEW
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Expert trust badge */}
                <div className="mt-6 flex justify-center">
                  <ExpertBadge variant="light" />
                </div>
              </>
            )}

            {/* Step 2: Product-specific questions */}
            {currentStep === 2 && (
              <>
                <div className="text-center mb-4">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
                    {productHasFullFlow(selectedProduct) ? 'A few quick questions' : 'Where are you located?'}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    {productHasFullFlow(selectedProduct) 
                      ? `Help us find the best ${getProductName()} options for you`
                      : `We'll connect you with ${getProductName()} specialists in your area`
                    }
                  </p>
                </div>

                <form onSubmit={handleStep2Submit} className="space-y-4">
                  {/* Address Input - For all products */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-2">
                      <MapPin className="w-4 h-4 text-cyo-orange" />
                      What's your address?
                    </label>
                    <AddressAutocomplete
                      value={address}
                      onChange={setAddress}
                      onSelect={(selected) => {
                        setAddress(selected.address);
                        if (selected.postcode) {
                          setPostcode(selected.postcode);
                        }
                      }}
                      placeholder="Start typing your address..."
                    />
                    
                    {/* Show extracted postcode */}
                    {postcode && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Postcode:</span>
                        <span className="text-xs font-semibold text-cyo-orange bg-orange-50 px-2 py-1 rounded">
                          {postcode}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Monthly Electricity Bill - For Solar and Battery only */}
                  {(selectedProduct === 'solar' || selectedProduct === 'battery') && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-3">
                        <DollarSign className="w-4 h-4 text-cyo-orange" />
                        What's your monthly electricity bill?
                      </label>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-cyo-orange">${monthlyBill}</span>
                          <span className="text-xs text-gray-500">per month</span>
                        </div>
                        
                        <Slider
                          value={[monthlyBill]}
                          onValueChange={(value) => setMonthlyBill(value[0])}
                          min={50}
                          max={1000}
                          step={10}
                          className="py-1"
                        />
                        
                        <div className="flex justify-between text-xs text-gray-400">
                          <span>$50</span>
                          <span>$500</span>
                          <span>$1,000+</span>
                        </div>

                        {/* Quick select buttons */}
                        <div className="flex flex-wrap gap-2">
                          {[100, 200, 300, 400, 500].map((amount) => (
                            <button
                              key={amount}
                              type="button"
                              onClick={() => setMonthlyBill(amount)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                monthlyBill === amount
                                  ? 'bg-cyo-orange text-white shadow-md'
                                  : 'bg-white text-gray-600 border border-gray-200 hover:border-cyo-orange hover:text-cyo-orange'
                              }`}
                            >
                              ${amount}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Battery-specific questions */}
                  {selectedProduct === 'battery' && (
                    <>
                      {/* Current Solar System Size */}
                      <div className="bg-gray-50 rounded-xl p-4">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-3">
                          <Sun className="w-4 h-4 text-cyo-orange" />
                          What size is your current solar system?
                        </label>
                        
                        <div className="grid grid-cols-3 gap-2">
                          {SOLAR_SYSTEM_SIZES.map((size) => (
                            <button
                              key={size.value}
                              type="button"
                              onClick={() => setSolarSystemSize(size.value)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                solarSystemSize === size.value
                                  ? 'bg-cyo-orange text-white shadow-md'
                                  : 'bg-white text-gray-600 border border-gray-200 hover:border-cyo-orange hover:text-cyo-orange'
                              }`}
                            >
                              {size.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* How long have you had solar - only show if user has solar */}
                      {solarSystemSize && solarSystemSize !== 'none' && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-3">
                            <Clock className="w-4 h-4 text-cyo-orange" />
                            How long have you had your solar system?
                          </label>
                          
                          <div className="grid grid-cols-3 gap-2">
                            {YEARS_OPTIONS.map((option) => (
                              <button
                              key={option.value}
                              type="button"
                              onClick={() => setSolarYears(option.value)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                solarYears === option.value
                                  ? 'bg-cyo-orange text-white shadow-md'
                                  : 'bg-white text-gray-600 border border-gray-200 hover:border-cyo-orange hover:text-cyo-orange'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      )}
                    </>
                  )}

                  {error && (
                    <p className="text-red-500 text-sm text-center">{error}</p>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={goBack}
                      className="flex-1 border-2 border-gray-200 text-gray-600 hover:border-gray-300 font-semibold py-5 text-base rounded-xl transition-all duration-300"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      data-testid="show-options-btn"
                      className="flex-[2] bg-cyo-orange hover:bg-cyo-orange-dark text-white font-semibold py-5 text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {productHasFullFlow(selectedProduct) ? 'Finding Options...' : 'Processing...'}
                        </>
                      ) : (
                        <>
                          {productHasFullFlow(selectedProduct) ? 'Show My Options' : 'Get My Quote'}
                          <Zap className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}

            <p className="text-center text-xs text-gray-400 mt-3">
              Free comparison • No obligation • Takes 2 minutes
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
